const ETHEREUM_MAINNET_CHAIN_ID = "0x1";

export const NFT_WALLET_TARGETS = Object.freeze({
  phantom: {
    provider: "phantom",
    label: "Phantom",
    network: "solana-mainnet",
    networkLabel: "Solana Mainnet",
  },
  metamask: {
    provider: "metamask",
    label: "MetaMask",
    network: "ethereum-mainnet",
    networkLabel: "Ethereum Mainnet",
  },
});

export const shortenWalletAddress = (address = "") =>
  address ? `${address.slice(0, 6)}…${address.slice(-6)}` : "";

const providerErrorCode = (error) => {
  if (Number(error?.code) === 4001) return "permission_rejected";
  if (/reject|denied/i.test(String(error?.message || ""))) return "permission_rejected";
  return "connection_failed";
};

export const getWalletConnectionMessage = (code, label) => {
  if (code === "provider_unavailable") {
    return `${label} was not found. Install or enable the wallet, then try again.`;
  }
  if (code === "permission_rejected") {
    return `${label} did not grant account access. Try again when you are ready.`;
  }
  return `${label} could not connect. Check the wallet and try again.`;
};

export function getPhantomProvider(browserWindow = window) {
  const provider = browserWindow?.phantom?.solana || browserWindow?.solana;
  return provider?.isPhantom ? provider : null;
}

export async function discoverMetaMaskProvider(browserWindow = window) {
  const announced = [];
  const onAnnounce = (event) => {
    const detail = event?.detail;
    if (detail?.provider) announced.push(detail);
  };

  browserWindow?.addEventListener?.("eip6963:announceProvider", onAnnounce);
  try {
    browserWindow?.dispatchEvent?.(new Event("eip6963:requestProvider"));
    await new Promise((resolve) => setTimeout(resolve, 100));
  } finally {
    browserWindow?.removeEventListener?.("eip6963:announceProvider", onAnnounce);
  }

  const eip6963 = announced.find(
    ({ info, provider }) =>
      provider?.isMetaMask ||
      /metamask/i.test(String(info?.rdns || "")) ||
      /metamask/i.test(String(info?.name || "")),
  )?.provider;
  if (eip6963) return eip6963;

  const ethereum = browserWindow?.ethereum;
  const providers = Array.isArray(ethereum?.providers) ? ethereum.providers : [];
  return providers.find((provider) => provider?.isMetaMask) ||
    (ethereum?.isMetaMask ? ethereum : null);
}

export async function connectNftWallet(providerKey, browserWindow = window) {
  if (providerKey === "phantom") {
    const provider = getPhantomProvider(browserWindow);
    if (!provider) {
      return { ok: false, code: "provider_unavailable" };
    }
    try {
      const response = await provider.connect();
      const address = response?.publicKey?.toString?.() || provider.publicKey?.toString?.();
      if (!address) throw new Error("No Solana address returned.");
      return { ok: true, address, chainId: "solana-mainnet", providerInstance: provider };
    } catch (error) {
      return { ok: false, code: providerErrorCode(error), providerInstance: provider };
    }
  }

  const provider = await discoverMetaMaskProvider(browserWindow);
  if (!provider) {
    return { ok: false, code: "provider_unavailable" };
  }
  try {
    const accounts = await provider.request({ method: "eth_requestAccounts" });
    const address = accounts?.[0];
    if (!address) throw new Error("No Ethereum address returned.");
    const chainId = await provider.request({ method: "eth_chainId" });
    return {
      ok: true,
      address,
      chainId: String(chainId || "").toLowerCase(),
      wrongNetwork: String(chainId || "").toLowerCase() !== ETHEREUM_MAINNET_CHAIN_ID,
      providerInstance: provider,
    };
  } catch (error) {
    return { ok: false, code: providerErrorCode(error), providerInstance: provider };
  }
}

export async function checkMetaMaskMainnet(provider) {
  if (!provider?.request) return false;
  const chainId = await provider.request({ method: "eth_chainId" });
  return String(chainId || "").toLowerCase() === ETHEREUM_MAINNET_CHAIN_ID;
}
